import { useEffect, useRef, useState } from "react";
import { cn } from "../../../lib/utils.js";
import { OrbAudioAnalyzer } from "./OrbAudioAnalyzer.js";
import { OrbRenderer, webglAvailable } from "./OrbRenderer.js";
import type { OrbProps, OrbState } from "./orb.types.js";

export type { OrbProps, OrbState } from "./orb.types.js";
export { createPlaybackAnalyser } from "./OrbAudioAnalyzer.js";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * A living, GPU-drawn orb that shows what the AI is doing: idle, listening, thinking or speaking.
 * While speaking it follows the voice: pass an AnalyserNode on the real playback audio, or write a
 * 0..1 level into `levelRef` (or `audioLevel`). It does not touch the microphone.
 *
 * Size it with `size` (px) or give the parent a size. Black-backed placement looks best; the
 * canvas itself is transparent.
 */
export function AIOrb({ state, audioLevel, levelRef, audioAnalyser, className, size }: OrbProps) {
  const host = useRef<HTMLDivElement>(null);
  // The render loop reads these every frame; changing them never re-renders React.
  const stateRef = useRef<OrbState>(state);
  const levelPropRef = useRef(audioLevel ?? 0);
  const externalLevel = useRef(levelRef);
  const analyzer = useRef<OrbAudioAnalyzer | null>(null);
  const [fallback, setFallback] = useState(false);

  stateRef.current = state;
  levelPropRef.current = audioLevel ?? 0;
  externalLevel.current = levelRef;

  useEffect(() => {
    analyzer.current = audioAnalyser ? new OrbAudioAnalyzer(audioAnalyser) : null;
    return () => { analyzer.current = null; };
  }, [audioAnalyser]);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    if (!webglAvailable()) { setFallback(true); return; }
    let renderer: OrbRenderer;
    try {
      renderer = new OrbRenderer({
        container: el,
        getState: () => stateRef.current,
        getAudio: () => {
          const real = analyzer.current?.read();
          if (real) return { level: real.level, low: real.low * 3, mid: real.mid * 3, high: real.high * 4 };
          // No analyser (browser voice, or the dev slider): one level, shared by every colour.
          const l = externalLevel.current?.current ?? levelPropRef.current;
          return { level: l, low: l, mid: l * 0.8, high: l * 0.6 };
        },
        reducedMotion: prefersReducedMotion(),
      });
    } catch {
      setFallback(true);
      return;
    }
    renderer.start();
    return () => renderer.destroy();
  }, []);

  return (
    <div
      ref={host}
      // Dimensions are caller-supplied, as on other ui/ primitives.
      style={size ? { width: size, height: size } : undefined}
      className={cn("relative aspect-square", className)}
      role="img"
      aria-label={`Assistant is ${state}`}
    >
      {fallback && (
        <div
          className={cn(
            "absolute inset-[18%] rounded-full bg-[radial-gradient(circle_at_35%_30%,#27A6FF_0%,#174BFF_35%,#080E50_75%)] shadow-[0_0_40px_8px_rgba(23,75,255,0.45)]",
            state !== "idle" && "motion-safe:animate-pulse",
          )}
        />
      )}
    </div>
  );
}
